import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { toast } from "react-toastify";
import { apiClient } from "../../api/client";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { TokenPillEditor } from "../../features/aotd/sections/nextAotd/TokenPillEditor";
import { useConfirm } from "../../ui/components/ConfirmDialog";
import { segmentEmojis } from "../../lib/emojiSegments";
import { SERVER_URL } from "../../config/env";

type Rect = { x: number; y: number; w: number; h: number };

type ZoneKind =
  | "aotd-img"
  | "image"
  | "title"
  | "author"
  | "emojis"
  | "meta"
  | "owner"
  | "bottomBar"
  | "qr"
  | "text"
  | "separator"
  | "rect";

type Zone = {
  id: string;
  kind: ZoneKind;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
  rotation?: 0 | 90 | 180 | 270;
  visible: boolean;
  align?: "left" | "center" | "right";
  vAlign?: "top" | "middle" | "bottom";
  fontSize?: number;
  fontWeight?: "normal" | "bold";
  fontFamily?: string;
  color?: string;
  bgColor?: string;
  padding?: number;
  adaptive?: boolean;
  text?: string;
  imageFit?: "cover" | "contain";
  imageOffsetX?: number;
  imageOffsetY?: number;
  qrPayload?: string;
};

type FontFamilyEntry = {
  key: string;
  label: string;
  monospace: boolean;
  weights: Array<"normal" | "bold">;
};

const DEFAULT_FONT_KEY = "JetBrainsMono";

// Device-side palette for the Pimoroni Spectra 6 panel — mirrors the
// `getDeviceColors("spectra6")` list shipped by epdoptimize on the backend.
// The render pipeline snaps every pixel to the nearest of these, so the
// editor preview must do the same to stay WYSIWYG with the panel output.
const SPECTRA6_PALETTE = [
  "#000000", // black
  "#FFFFFF", // white
  "#0000FF", // blue
  "#00FF00", // green
  "#FF0000", // red
  "#FFFF00", // yellow
] as const;

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([a-fA-F\d])([a-fA-F\d])([a-fA-F\d])$|^#?([a-fA-F\d]{2})([a-fA-F\d]{2})([a-fA-F\d]{2})$/.exec(hex.trim());
  if (!m) return null;
  if (m[1]) {
    return [parseInt(m[1] + m[1], 16), parseInt(m[2] + m[2], 16), parseInt(m[3] + m[3], 16)];
  }
  return [parseInt(m[4], 16), parseInt(m[5], 16), parseInt(m[6], 16)];
}

/**
 * Snap an arbitrary hex colour to the nearest Spectra-6 panel primary.
 * Uses simple Euclidean distance in sRGB — matches how the render
 * pipeline quantises colours before dithering.
 */
function snapToSpectra6(hex: string | undefined | null): string {
  const rgb = hexToRgb(hex ?? "#000000");
  if (!rgb) return "#000000";
  let best = SPECTRA6_PALETTE[0] as string;
  let bestDist = Infinity;
  for (const p of SPECTRA6_PALETTE) {
    const prgb = hexToRgb(p)!;
    const dr = rgb[0] - prgb[0];
    const dg = rgb[1] - prgb[1];
    const db = rgb[2] - prgb[2];
    const d = dr * dr + dg * dg + db * db;
    if (d < bestDist) {
      best = p;
      bestDist = d;
    }
  }
  return best;
}

type LayoutDoc = {
  panelProfile: string;
  safeArea: Rect;
  rotation: 0 | 90 | 180 | 270;
  zones: Zone[];
  revision?: number;
};

type PanelProfile = {
  key: string;
  label: string;
  width: number;
  height: number;
  bpp?: number;
  palette?: string;
  nativeOrientation?: string;
};

const ZONE_KINDS: ZoneKind[] = [
  "aotd-img", // canonical image area / safe-area rectangle (required, exactly one)
  "image",    // legacy alias — kept visible so old layouts remain editable
  "title",
  "author",
  "emojis",
  "meta",
  "owner",
  "bottomBar",
  "qr",
  "text",
  "separator",
  "rect",
];

const ROTATIONS: Array<0 | 90 | 180 | 270> = [0, 90, 180, 270];

type TokenKey =
  | "title"
  | "author"
  | "authorEmoji"
  | "date"
  | "ownerUsername"
  | "deviceShortId"
  | "firmwareVersion"
  | "refreshAtUtc";

const TOKEN_SAMPLES: Record<TokenKey, string> = {
  title: "Sunset Over the Sierra",
  author: "alice",
  authorEmoji: "🦊",
  date: "Tuesday, April 21",
  ownerUsername: "nassos",
  deviceShortId: "PXKQ",
  firmwareVersion: "aotd-irl-2.2.0",
  refreshAtUtc: "14:35 UTC",
};

const TOKENS_BY_KIND: Record<ZoneKind, ReadonlyArray<TokenKey>> = {
  title: ["title", "date"],
  author: ["author", "authorEmoji"],
  meta: ["date", "deviceShortId", "firmwareVersion"],
  owner: ["ownerUsername"],
  bottomBar: ["date", "ownerUsername", "deviceShortId"],
  text: ["title", "author", "authorEmoji", "date", "ownerUsername", "deviceShortId", "firmwareVersion", "refreshAtUtc"],
  emojis: [],
  "aotd-img": [],
  image: [],
  qr: [],
  separator: [],
  rect: [],
};

// A pair of a native color picker and a hex/name text input. Writes back on
// change from either control. Normalizes the picker to a safe #rrggbb so the
// native UI opens correctly even when the zone carries a css-name or empty.
function ColorInputRow({
  value,
  onChange,
}: {
  value?: string;
  onChange: (v: string | undefined) => void;
}) {
  const pickerHex = /^#[0-9a-f]{6}$/i.test(value ?? "") ? (value as string) : "#000000";
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      <input
        type="color"
        value={pickerHex}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: 36,
          height: 28,
          padding: 0,
          border: "1px solid #bbb",
          background: "#fff",
          cursor: "pointer",
        }}
        aria-label="Color picker"
      />
      <input
        type="text"
        value={value ?? ""}
        placeholder="#rrggbb or css name"
        onChange={(e) => onChange(e.target.value || undefined)}
        style={{ flex: 1 }}
      />
    </div>
  );
}

function substituteTokens(template: string): string {
  return template
    .replace(/\{\{title\}\}/g, TOKEN_SAMPLES.title)
    .replace(/\{\{author\}\}/g, TOKEN_SAMPLES.author)
    .replace(/\{\{authorEmoji\}\}/g, TOKEN_SAMPLES.authorEmoji)
    .replace(/\{\{date\}\}/g, TOKEN_SAMPLES.date)
    .replace(/\{\{ownerUsername\}\}/g, TOKEN_SAMPLES.ownerUsername)
    .replace(/\{\{deviceShortId\}\}/g, TOKEN_SAMPLES.deviceShortId)
    .replace(/\{\{firmwareVersion\}\}/g, TOKEN_SAMPLES.firmwareVersion);
}

const Wrap = styled.div`
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  background: #f0f0f0;
  padding: 1rem;
  overflow: hidden;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  box-sizing: border-box;
  @media (max-width: 960px) {
    height: auto;
    overflow: visible;
  }
`;

const TopBar = styled.div`
  max-width: 1400px;
  margin: 0 auto 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const BackLink = styled(Link)`
  display: inline-block;
  padding: 0.35rem 0.65rem;
  border: 1px solid #232323;
  background: #fff;
  color: #232323;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.8rem;
  box-shadow: 3px 3px #232323;
  &:hover {
    background: #e8e8e8;
  }
`;

const Grid = styled.div`
  flex: 1;
  min-height: 0;
  max-width: 1600px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 380px;
  gap: 1rem;
  align-items: stretch;
  @media (max-width: 1100px) {
    grid-template-columns: 260px minmax(0, 1fr);
  }
  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    align-items: start;
  }
`;

const CanvasCard = styled.div`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px #232323;
  padding: 0.75rem;
  height: 100%;
  min-height: 0;
  overflow: auto;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  @media (max-width: 960px) {
    height: auto;
  }
`;

const SvgHost = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const CanvasEl = styled.svg`
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  background: #e4e4e4;
  border: 1px solid #232323;
  touch-action: none;
`;

const Sidebar = styled.aside`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 6px 6px #232323;
  padding: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  height: 100%;
  min-height: 0;
  overflow: auto;
  position: static;
  box-sizing: border-box;
  @media (max-width: 960px) {
    height: auto;
    max-height: none;
  }
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.75rem;
  font-weight: 700;
  input,
  select,
  textarea {
    font: inherit;
    padding: 0.25rem 0.35rem;
    border: 1px solid #232323;
    border-radius: 0;
  }
`;

const Row = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
  align-items: flex-end;
`;

const Btn = styled.button`
  font: inherit;
  cursor: pointer;
  padding: 0.4rem 0.65rem;
  border: 1px solid #232323;
  background: #fff;
  font-weight: 700;
  font-size: 0.8rem;
  box-shadow: 3px 3px #232323;
  &:hover:not(:disabled) {
    background: #e8e8e8;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled(Btn)`
  background: #c9febe;
`;

const Err = styled.p`
  color: crimson;
  margin: 0;
  font-size: 0.8rem;
`;

const PreviewBox = styled.div`
  border: 1px dashed #232323;
  padding: 0.35rem;
  background: #fafafa;
  img {
    max-width: 100%;
    height: auto;
    display: block;
    image-rendering: pixelated;
    image-rendering: crisp-edges;
  }
`;

const TextPreviewBox = styled.div`
  border: 1px dashed #232323;
  background: #ffffff;
  padding: 1rem;
  margin-top: 0.25rem;
  min-height: 120px;
  max-height: 480px;
  overflow: auto;
  word-break: break-word;
  white-space: pre-wrap;
  display: flex;
`;

const TokenChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin: 0.25rem 0 0.4rem;
`;

const TokenChip = styled.button`
  font: inherit;
  cursor: pointer;
  padding: 0.1rem 0.4rem;
  border: 1px dashed #232323;
  background: #f7f7f7;
  font-size: 0.7rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  color: #232323;
  &:hover {
    background: #ffe9a8;
  }
`;

const TokenChipsLabel = styled.small`
  display: block;
  color: #666;
  font-size: 0.7rem;
  margin-top: 0.25rem;
`;

const TextPreviewLabel = styled.small`
  display: block;
  color: #666;
  font-size: 0.7rem;
  margin-top: 0.5rem;
`;

const PreviewZoomBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  color: #555;
  margin-top: 0.25rem;
`;

const PreviewZoomBtn = styled.button<{ active?: boolean }>`
  border: 1px solid #232323;
  background: ${(p) => (p.active ? "#232323" : "#ffffff")};
  color: ${(p) => (p.active ? "#ffffff" : "#232323")};
  padding: 0.1rem 0.4rem;
  font-family: inherit;
  font-size: 0.7rem;
  cursor: pointer;
  &:hover {
    background: ${(p) => (p.active ? "#232323" : "#f3f3f3")};
  }
`;

const ZoneList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 160px;
  overflow: auto;
  border: 1px solid #232323;
  li {
    padding: 0.3rem 0.45rem;
    border-bottom: 1px solid #ddd;
    cursor: pointer;
    font-size: 0.75rem;
    display: flex;
    justify-content: space-between;
    gap: 0.35rem;
  }
  li:last-child {
    border-bottom: none;
  }
  li[data-sel="1"] {
    background: #fffbd6;
  }
`;

const DelBtn = styled.button`
  font: inherit;
  cursor: pointer;
  padding: 0.1rem 0.35rem;
  border: 1px solid #232323;
  background: #fff;
  color: #b00020;
  font-weight: 700;
  font-size: 0.7rem;
  line-height: 1.2;
`;

const DeleteZoneBtn = styled.button`
  font: inherit;
  cursor: pointer;
  margin-top: 0.5rem;
  padding: 0.4rem 0.65rem;
  border: 1px solid #b00020;
  background: #fff;
  color: #b00020;
  font-weight: 700;
  font-size: 0.8rem;
  box-shadow: 3px 3px 0 #b00020;
  &:hover:not(:disabled) {
    transform: translate(-1px, -1px);
    box-shadow: 4px 4px 0 #b00020;
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PreviewMetaNote = styled.p`
  margin: 0.35rem 0 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New",
    monospace;
  font-size: 0.75rem;
  color: #555;
`;

const LivePreviewLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  input {
    cursor: pointer;
  }
`;

const PreviewStatus = styled.div`
  font-size: 0.7rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const newZoneId = () => `z_${Math.random().toString(36).slice(2, 8)}`;

function emptyLayout(profileKey: string, w: number, h: number): LayoutDoc {
  return {
    panelProfile: profileKey,
    safeArea: { x: 0, y: 0, w, h },
    rotation: 0,
    zones: [],
  };
}

function clientToSvg(svg: SVGSVGElement, clientX: number, clientY: number) {
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  return pt.matrixTransform(ctm.inverse());
}

function normalizeZone(z: Partial<Zone> & { id: string; kind: ZoneKind; x: number; y: number; w: number; h: number }): Zone {
  return {
    ...z,
    z: typeof z.z === "number" ? z.z : 0,
    visible: z.visible !== false,
  } as Zone;
}

export default function AotdIrlAdminEditor() {
  const auth = useAppSelector((s) => s.auth);
  const confirm = useConfirm();
  const { deviceId: rawDeviceId = "" } = useParams<{ deviceId: string }>();
  const deviceId = rawDeviceId.trim();

  const [profiles, setProfiles] = useState<PanelProfile[]>([]);
  const [layout, setLayout] = useState<LayoutDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewMeta, setPreviewMeta] = useState<{ effectiveDate?: string; usedFallback?: boolean; rotation?: number }>({});
  const [previewLoading, setPreviewLoading] = useState(false);
  const [livePreview, setLivePreview] = useState(false);
  const [textPreviewZoom, setTextPreviewZoom] = useState<1 | 2 | 3 | 4>(2);
  const [fontFamilies, setFontFamilies] = useState<FontFamilyEntry[]>([]);
  const [snapGrid, setSnapGrid] = useState<number>(4);
  const [snapEnabled, setSnapEnabled] = useState<boolean>(true);
  const [saving, setSaving] = useState(false);
  // Bounded undo history — snapshots of the full layout JSON on every user
  // mutation. Cmd/Ctrl+Z pops the most recent snapshot and restores it.
  const historyRef = useRef<LayoutDoc[]>([]);
  const HISTORY_LIMIT = 50;
  const layoutLoadArmedRef = useRef(false);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<{
    type: "move" | "resize";
    id: string;
    start: { x: number; y: number };
    orig: Zone;
  } | null>(null);
  const textAreaRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});

  const profile = useMemo(
    () => profiles.find((p) => p.key === layout?.panelProfile) ?? profiles[0],
    [profiles, layout]
  );

  const selected = useMemo(
    () => layout?.zones.find((z) => z.id === selectedId) ?? null,
    [layout, selectedId]
  );

  // Map a zone's fontFamily key to the CSS family label that matches the
  // @font-face declarations above. Returns a CSS font-family stack.
  const cssFamilyForZone = useCallback(
    (fontKey?: string): string => {
      const key = fontKey ?? DEFAULT_FONT_KEY;
      const entry = fontFamilies.find((f) => f.key === key)
        ?? fontFamilies.find((f) => f.key === DEFAULT_FONT_KEY);
      const label = entry?.label ?? "JetBrains Mono";
      return `'${label}', ui-monospace, SFMono-Regular, Menlo, Monaco, monospace`;
    },
    [fontFamilies]
  );

  // Load the curated font registry and inject a @font-face for each available
  // weight so the in-editor SVG matches what the server will render. The
  // backend streams the same TTF binaries it registered with node-canvas.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ fonts?: FontFamilyEntry[] }>("/api/aotd-irl/admin/fonts");
        if (cancelled) return;
        const fonts = res.data?.fonts ?? [];
        setFontFamilies(fonts);
        const styleEl = document.createElement("style");
        styleEl.dataset.aotdIrlFonts = "1";
        const base = SERVER_URL || "";
        const declarations: string[] = [];
        for (const f of fonts) {
          for (const w of f.weights) {
            const variant = w === "bold" ? "bold" : "regular";
            declarations.push(
              `@font-face { font-family: '${f.label}'; font-weight: ${w === "bold" ? 700 : 400}; font-style: normal; src: url('${base}/api/aotd-irl/admin/fonts/${encodeURIComponent(f.key)}/${variant}.ttf') format('truetype'); font-display: swap; }`
            );
          }
        }
        styleEl.textContent = declarations.join("\n");
        document.head.appendChild(styleEl);
        return () => {
          document.head.removeChild(styleEl);
        };
      } catch (e) {
        console.warn("[aotd-irl] font registry fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!deviceId) return;
    layoutLoadArmedRef.current = false;
    let cancelled = false;
    (async () => {
      try {
        const profilesRes = await apiClient.get("/api/aotd-irl/admin/panel-profiles");
        if (cancelled) return;
        const profileList: PanelProfile[] = profilesRes.data?.profiles ?? [];
        setProfiles(profileList);

        let existing: LayoutDoc | null = null;
        try {
          const layoutRes = await apiClient.get(
            `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/layout`
          );
          existing = layoutRes.data?.layout ?? null;
        } catch (e: unknown) {
          const ax = e as { response?: { status?: number } };
          if (ax.response?.status !== 404) throw e;
        }

        if (cancelled) return;

        if (existing) {
          const zones = (existing.zones ?? []).map((z: Zone) => normalizeZone(z));
          setLayout({
            ...existing,
            zones,
            safeArea: existing.safeArea ?? { x: 0, y: 0, w: 1200, h: 1600 },
            rotation: (existing.rotation ?? 0) as LayoutDoc["rotation"],
          });
        } else if (profileList[0]) {
          const p0 = profileList[0];
          setLayout(emptyLayout(p0.key, p0.width, p0.height));
        } else {
          setError("no panel profiles");
        }
      } catch (e: unknown) {
        const msg =
          e && typeof e === "object" && "message" in e ? String((e as Error).message) : "failed to load";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) {
          layoutLoadArmedRef.current = true;
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deviceId]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  // Snapshot the current layout into the undo stack. Call BEFORE a user
  // gesture (drag start, nudge keypress, zone add/delete) — not on every
  // setLayout call, since drags emit many updates per second.
  const pushHistory = useCallback(() => {
    setLayout((cur) => {
      if (cur) {
        historyRef.current.push(JSON.parse(JSON.stringify(cur)) as LayoutDoc);
        if (historyRef.current.length > HISTORY_LIMIT) {
          historyRef.current.shift();
        }
      }
      return cur;
    });
  }, []);

  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (prev) {
      setLayout(prev);
      toast.info("undone", { position: toast.POSITION.BOTTOM_LEFT, autoClose: 800 });
    }
  }, []);

  const snapValue = useCallback(
    (v: number) => {
      if (!snapEnabled || snapGrid <= 0) return v;
      return Math.round(v / snapGrid) * snapGrid;
    },
    [snapEnabled, snapGrid]
  );

  const updateLayout = useCallback((patch: Partial<LayoutDoc>) => {
    setLayout((cur) => (cur ? { ...cur, ...patch } : cur));
  }, []);

  const updateZone = useCallback((id: string, patch: Partial<Zone>) => {
    setLayout((cur) => {
      if (!cur) return cur;
      return { ...cur, zones: cur.zones.map((z) => (z.id === id ? normalizeZone({ ...z, ...patch }) : z)) };
    });
  }, []);

  const insertTokenAtCursor = useCallback(
    (zoneId: string, token: TokenKey) => {
      const placeholder = `{{${token}}}`;
      const ta = textAreaRefs.current[zoneId];
      const z = layout?.zones.find((zz) => zz.id === zoneId);
      if (!z) return;
      if (ta && document.activeElement === ta && typeof ta.selectionStart === "number") {
        const start = ta.selectionStart;
        const end = ta.selectionEnd ?? start;
        const before = (z.text ?? "").slice(0, start);
        const after = (z.text ?? "").slice(end);
        const next = before + placeholder + after;
        updateZone(zoneId, { text: next });
        requestAnimationFrame(() => {
          const t2 = textAreaRefs.current[zoneId];
          if (t2) {
            const pos = start + placeholder.length;
            t2.focus();
            t2.setSelectionRange(pos, pos);
          }
        });
      } else {
        updateZone(zoneId, { text: (z.text ?? "") + placeholder });
      }
    },
    [layout, updateZone]
  );

  const addZone = useCallback(() => {
    pushHistory();
    const id = newZoneId();
    setLayout((cur) => {
      if (!cur) return cur;
      const maxZ = cur.zones.reduce((m, z) => Math.max(m, z.z ?? 0), -1);
      const z: Zone = {
        id,
        kind: "text",
        x: 20,
        y: 20,
        w: 200,
        h: 60,
        z: maxZ + 1,
        visible: true,
        text: "Hello",
        fontSize: 24,
        align: "left",
        color: "#000000",
      };
      return { ...cur, zones: [...cur.zones, z] };
    });
    setSelectedId(id);
  }, [pushHistory]);

  const deleteZone = useCallback((id: string) => {
    pushHistory();
    setLayout((cur) => (cur ? { ...cur, zones: cur.zones.filter((z) => z.id !== id) } : cur));
    setSelectedId(null);
  }, [pushHistory]);

  const duplicateZone = useCallback((id: string) => {
    pushHistory();
    let newId: string | null = null;
    setLayout((cur) => {
      if (!cur) return cur;
      const src = cur.zones.find((z) => z.id === id);
      if (!src) return cur;
      const maxZ = cur.zones.reduce((m, z) => Math.max(m, z.z ?? 0), -1);
      newId = newZoneId();
      const copy: Zone = {
        ...src,
        id: newId,
        x: src.x + 10,
        y: src.y + 10,
        z: maxZ + 1,
      };
      return { ...cur, zones: [...cur.zones, copy] };
    });
    if (newId) setSelectedId(newId);
  }, [pushHistory]);

  // Move a zone up/down in z-order by swapping z values with the neighbour.
  const moveZoneZ = useCallback((id: string, dir: "up" | "down") => {
    pushHistory();
    setLayout((cur) => {
      if (!cur) return cur;
      const sorted = [...cur.zones].sort((a, b) => a.z - b.z);
      const i = sorted.findIndex((z) => z.id === id);
      if (i < 0) return cur;
      const j = dir === "up" ? i + 1 : i - 1;
      if (j < 0 || j >= sorted.length) return cur;
      const a = sorted[i];
      const b = sorted[j];
      const newZones = cur.zones.map((z) =>
        z.id === a.id ? { ...z, z: b.z } : z.id === b.id ? { ...z, z: a.z } : z
      );
      return { ...cur, zones: newZones };
    });
  }, [pushHistory]);

  const onPointerDownZone = useCallback(
    (e: React.PointerEvent, z: Zone) => {
      e.stopPropagation();
      setSelectedId(z.id);
      const svg = svgRef.current;
      if (!svg) return;
      const p = clientToSvg(svg, e.clientX, e.clientY);
      pushHistory();
      dragRef.current = { type: "move", id: z.id, start: p, orig: { ...z } };
      (e.currentTarget as SVGElement).setPointerCapture(e.pointerId);
    },
    [pushHistory]
  );

  const onPointerDownHandle = useCallback((e: React.PointerEvent, z: Zone) => {
    e.stopPropagation();
    setSelectedId(z.id);
    const svg = svgRef.current;
    if (!svg) return;
    const p = clientToSvg(svg, e.clientX, e.clientY);
    pushHistory();
    dragRef.current = { type: "resize", id: z.id, start: p, orig: { ...z } };
    (e.currentTarget as SVGElement).setPointerCapture(e.pointerId);
  }, [pushHistory]);

  const onZonePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const svg = svgRef.current;
      if (!svg) return;
      const p = clientToSvg(svg, e.clientX, e.clientY);
      const dx = p.x - d.start.x;
      const dy = p.y - d.start.y;
      if (d.type === "move") {
        updateZone(d.id, {
          x: snapValue(d.orig.x + dx),
          y: snapValue(d.orig.y + dy),
        });
      } else {
        updateZone(d.id, {
          w: Math.max(8, snapValue(d.orig.w + dx)),
          h: Math.max(8, snapValue(d.orig.h + dy)),
        });
      }
    },
    [updateZone, snapValue]
  );

  const onZonePointerUp = useCallback((e: React.PointerEvent) => {
    dragRef.current = null;
    try {
      (e.currentTarget as SVGElement).releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!layout) return;
    setSaving(true);
    setError(null);
    try {
      const res = await apiClient.put(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/layout`, {
        panelProfile: layout.panelProfile,
        safeArea: layout.safeArea,
        rotation: layout.rotation,
        zones: layout.zones.map((z) => normalizeZone(z)),
      });
      const next = res.data?.layout;
      if (next) {
        setLayout({
          ...next,
          zones: (next.zones ?? []).map((z: Zone) => normalizeZone(z)),
        });
      }
      toast.success("layout saved", { position: toast.POSITION.BOTTOM_LEFT });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      setError(ax?.response?.data?.error || ax?.message || "save failed");
    } finally {
      setSaving(false);
    }
  }, [deviceId, layout]);

  const handleReset = useCallback(async () => {
    const ok = await confirm({
      title: "Reset layout to default?",
      message: "All local edits will be replaced with the panel-profile defaults. You can't undo this.",
      confirmLabel: "Reset",
      tone: "danger",
    });
    if (!ok) return;
    setError(null);
    try {
      const res = await apiClient.post(`/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/layout/reset`);
      const next = res.data?.layout;
      if (next) {
        setLayout({
          ...next,
          zones: (next.zones ?? []).map((z: Zone) => normalizeZone(z)),
        });
        toast.info("layout reset", { position: toast.POSITION.BOTTOM_LEFT });
      }
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      setError(ax?.response?.data?.error || ax?.message || "reset failed");
    }
  }, [deviceId, confirm]);

  const handleSavePreset = useCallback(async () => {
    if (!layout || !profile) return;
    const ok = await confirm({
      title: `Save preset for ${profile.label}?`,
      message: `This overwrites any existing ${profile.width}×${profile.height} preset. Affects every new device on this panel profile.`,
      confirmLabel: "Save preset",
    });
    if (!ok) return;
    try {
      await apiClient.put(`/api/aotd-irl/admin/layout-presets/${encodeURIComponent(layout.panelProfile)}`, {
        safeArea: layout.safeArea,
        rotation: layout.rotation,
        zones: layout.zones,
      });
      toast.success(`preset saved for ${layout.panelProfile}`, { position: toast.POSITION.BOTTOM_LEFT });
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      setError(ax?.response?.data?.error || ax?.message || "save preset failed");
    }
  }, [layout, profile, confirm]);

  const handleLoadPreset = useCallback(async () => {
    if (!layout || !profile || !deviceId) return;
    const ok = await confirm({
      title: `Load preset for ${profile.label}?`,
      message: `The ${profile.width}×${profile.height} preset will replace the current unsaved layout. Nothing is sent to the device until you click Save.`,
      confirmLabel: "Load preset",
    });
    if (!ok) return;
    try {
      const res = await apiClient.get(
        `/api/aotd-irl/admin/layout-presets/${encodeURIComponent(layout.panelProfile)}`
      );
      const preset = res.data?.preset;
      if (!preset) {
        setError("no preset for this dimension");
        return;
      }
      const zones = (preset.zones ?? []).map((z: Zone) => normalizeZone(z));
      setLayout({
        panelProfile: preset.panelProfile,
        safeArea: preset.safeArea,
        rotation: preset.rotation,
        zones,
        revision: preset.revision,
      });
      setSelectedId(null);
      toast.info(`preset loaded for ${preset.panelProfile}`, { position: toast.POSITION.BOTTOM_LEFT });
    } catch (e: unknown) {
      const ax = e as { response?: { status?: number; data?: { error?: string } }; message?: string };
      if (ax?.response?.status === 404) {
        setError("no preset set for this dimension yet");
        return;
      }
      setError(ax?.response?.data?.error || ax?.message || "load preset failed");
    }
  }, [layout, profile, deviceId, confirm]);

  const handlePreview = useCallback(async () => {
    if (!layout) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const res = await apiClient.post(
        `/api/aotd-irl/admin/devices/${encodeURIComponent(deviceId)}/layout/preview`,
        {
          layout: {
            panelProfile: layout.panelProfile,
            safeArea: layout.safeArea,
            rotation: layout.rotation,
            zones: layout.zones.map((z) => normalizeZone(z)),
            revision: layout.revision ?? 1,
          },
        },
        { responseType: "arraybuffer" }
      );
      const fallback = res.headers["x-aotd-preview-fallback"] === "1";
      const rawEffective = res.headers["x-aotd-preview-effective-date"];
      const effectiveDate = Array.isArray(rawEffective) ? rawEffective[0] : rawEffective;
      const rawRot = res.headers["x-aotd-rotation"];
      const rotStr = Array.isArray(rawRot) ? rawRot[0] : rawRot;
      const rotNum = typeof rotStr === "string" ? parseInt(rotStr, 10) : 0;
      setPreviewMeta({
        effectiveDate: typeof effectiveDate === "string" ? effectiveDate : undefined,
        usedFallback: fallback,
        rotation: Number.isFinite(rotNum) ? rotNum : 0,
      });
      const blob = new Blob([res.data], { type: "image/png" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e: unknown) {
      setPreviewMeta({});
      const ax = e as { response?: { data?: ArrayBuffer | { error?: string }; status?: number } };
      let msg = "preview failed";
      const data = ax.response?.data;
      if (data instanceof ArrayBuffer) {
        try {
          const text = new TextDecoder().decode(data);
          const j = JSON.parse(text) as { error?: string };
          msg = j.error || msg;
        } catch {
          /* keep default */
        }
      } else if (data && typeof data === "object" && "error" in data) {
        msg = String((data as { error: string }).error);
      }
      setError(msg);
    } finally {
      setPreviewLoading(false);
    }
  }, [deviceId, layout]);

  useEffect(() => {
    if (loading || !layout || !livePreview || !layoutLoadArmedRef.current) return;
    const t = window.setTimeout(() => {
      void handlePreview();
    }, 600);
    return () => window.clearTimeout(t);
  }, [layout, livePreview, loading, handlePreview]);

  useEffect(() => {
    const shouldIgnoreFocus = (): boolean => {
      const el = document.activeElement;
      const tag = el?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      if (el instanceof HTMLElement && el.isContentEditable) return true;
      return false;
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;

      // Cmd/Ctrl+Z — undo (works even when an input isn't focused)
      if (mod && !e.shiftKey && (e.key === "z" || e.key === "Z")) {
        if (shouldIgnoreFocus()) return;
        e.preventDefault();
        undo();
        return;
      }

      // Cmd/Ctrl+D — duplicate selected zone
      if (mod && (e.key === "d" || e.key === "D")) {
        if (shouldIgnoreFocus()) return;
        if (!selectedId) return;
        e.preventDefault();
        duplicateZone(selectedId);
        return;
      }

      // Delete/Backspace — delete selected zone (with modal confirm)
      if (e.key === "Delete" || e.key === "Backspace") {
        if (shouldIgnoreFocus()) return;
        if (!selectedId) return;
        e.preventDefault();
        const targetId = selectedId;
        void confirm({
          title: "Delete this zone?",
          message: "This cannot be undone (unless you haven't saved — reset restores it).",
          confirmLabel: "Delete",
          tone: "danger",
        }).then((ok) => {
          if (ok) deleteZone(targetId);
        });
        return;
      }

      // Arrow keys — nudge selected zone (1px, Shift+arrow = 10px)
      if (
        e.key === "ArrowUp" ||
        e.key === "ArrowDown" ||
        e.key === "ArrowLeft" ||
        e.key === "ArrowRight"
      ) {
        if (shouldIgnoreFocus()) return;
        if (!selectedId) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        pushHistory();
        setLayout((cur) => {
          if (!cur) return cur;
          return {
            ...cur,
            zones: cur.zones.map((z) => {
              if (z.id !== selectedId) return z;
              let dx = 0;
              let dy = 0;
              if (e.key === "ArrowUp") dy = -step;
              else if (e.key === "ArrowDown") dy = step;
              else if (e.key === "ArrowLeft") dx = -step;
              else if (e.key === "ArrowRight") dx = step;
              return { ...z, x: z.x + dx, y: z.y + dy };
            }),
          };
        });
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedId, deleteZone, confirm, duplicateZone, undo, pushHistory]);

  const onChangeProfile = useCallback(
    (key: string) => {
      const p = profiles.find((x) => x.key === key);
      if (!p) return;
      updateLayout({
        panelProfile: key,
        safeArea: { x: 0, y: 0, w: p.width, h: p.height },
      });
    },
    [profiles, updateLayout]
  );

  if (auth.bootstrapStatus !== "ready") {
    return null;
  }
  if (!auth.token) {
    return <Navigate to="/login" replace />;
  }
  if (!checkRole(auth.role ?? "", "admin")) {
    return <Navigate to="/" replace />;
  }

  if (!deviceId) {
    return (
      <Wrap>
        <Err>missing device id</Err>
      </Wrap>
    );
  }

  if (loading) {
    return (
      <Wrap>
        <p>Loading…</p>
      </Wrap>
    );
  }

  if (!layout || !profile) {
    return (
      <Wrap>
        <Err>{error || "No layout."}</Err>
      </Wrap>
    );
  }

  const W = profile.width;
  const H = profile.height;
  const zonesSorted = [...layout.zones].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
  const HANDLE = 14;

  return (
    <Wrap>
      <TopBar>
        <BackLink to="/aotd-irl/admin/devices">← devices</BackLink>
        <span style={{ fontSize: "0.85rem", fontWeight: 700 }}>
          layout · <code>{deviceId}</code>
        </span>
        <div style={{ flex: 1 }} />
        <BtnPrimary type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </BtnPrimary>
        <Btn type="button" onClick={handleReset}>
          Reset
        </Btn>
        <Btn type="button" onClick={handleSavePreset}>
          Save as preset
        </Btn>
        <Btn type="button" onClick={handleLoadPreset}>
          Load preset
        </Btn>
        <LivePreviewLabel>
          <input
            type="checkbox"
            checked={livePreview}
            onChange={(e) => setLivePreview(e.target.checked)}
          />
          live preview
        </LivePreviewLabel>
        <Btn type="button" onClick={handlePreview}>
          Preview PNG
        </Btn>
      </TopBar>

      <Grid>
        <Sidebar>
          <SidebarTitle>general</SidebarTitle>
          {error && <Err>{error}</Err>}

          <Field>
            panel profile
            <select value={layout.panelProfile} onChange={(e) => onChangeProfile(e.target.value)}>
              {profiles.map((p) => (
                <option key={p.key} value={p.key}>
                  {p.label} ({p.width}×{p.height})
                </option>
              ))}
            </select>
          </Field>

          <Field>
            rotation (°)
            <select
              value={layout.rotation}
              onChange={(e) =>
                updateLayout({ rotation: Number(e.target.value) as LayoutDoc["rotation"] })
              }
            >
              {ROTATIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </Field>

          <SidebarTitle>safe area</SidebarTitle>
          <Row>
            <Field style={{ flex: 1 }}>
              x
              <input
                type="number"
                value={layout.safeArea.x}
                onChange={(e) =>
                  updateLayout({
                    safeArea: { ...layout.safeArea, x: Number(e.target.value) },
                  })
                }
              />
            </Field>
            <Field style={{ flex: 1 }}>
              y
              <input
                type="number"
                value={layout.safeArea.y}
                onChange={(e) =>
                  updateLayout({
                    safeArea: { ...layout.safeArea, y: Number(e.target.value) },
                  })
                }
              />
            </Field>
          </Row>
          <Row>
            <Field style={{ flex: 1 }}>
              w
              <input
                type="number"
                value={layout.safeArea.w}
                onChange={(e) =>
                  updateLayout({
                    safeArea: { ...layout.safeArea, w: Number(e.target.value) },
                  })
                }
              />
            </Field>
            <Field style={{ flex: 1 }}>
              h
              <input
                type="number"
                value={layout.safeArea.h}
                onChange={(e) =>
                  updateLayout({
                    safeArea: { ...layout.safeArea, h: Number(e.target.value) },
                  })
                }
              />
            </Field>
          </Row>

          <SidebarTitle>grid</SidebarTitle>
          <Row>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
              <input
                type="checkbox"
                checked={snapEnabled}
                onChange={(e) => setSnapEnabled(e.target.checked)}
              />
              snap to grid
            </label>
            <input
              type="number"
              min={1}
              max={64}
              value={snapGrid}
              onChange={(e) => setSnapGrid(Math.max(1, Number(e.target.value) || 1))}
              style={{ width: 64 }}
              aria-label="Snap grid size in px"
            />
            <span style={{ color: "#888", fontSize: 11 }}>px</span>
          </Row>
          <small style={{ color: "#666", fontSize: 11 }}>
            arrow keys nudge selected zone (Shift = 10px)
          </small>
        </Sidebar>

        <CanvasCard>
          <SvgHost>
            <CanvasEl
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              width={W}
              height={H}
              onPointerDown={() => setSelectedId(null)}
            >
              <rect x={0} y={0} width={W} height={H} fill="#eeeeee" />
              <rect
                x={layout.safeArea.x}
                y={layout.safeArea.y}
                width={layout.safeArea.w}
                height={layout.safeArea.h}
                fill="rgba(201,254,190,0.25)"
                stroke="#232323"
                strokeWidth={2}
                strokeDasharray="8 6"
                pointerEvents="none"
              />
              {zonesSorted.map((z) => {
                const sel = z.id === selectedId;
                const isTextLike = ["text", "title", "author", "meta", "owner", "bottomBar"].includes(z.kind);
                const isEmojis = z.kind === "emojis";
                const fontSize = z.fontSize ?? 22;
                const padding = z.padding ?? 0;
                const align = z.align ?? "left";
                const vAlign = z.vAlign ?? "top";
                const textAnchor = align === "center" ? "middle" : align === "right" ? "end" : "start";
                const textX =
                  align === "center"
                    ? z.x + z.w / 2
                    : align === "right"
                      ? z.x + z.w - padding
                      : z.x + padding;
                const textBaselineY =
                  vAlign === "middle"
                    ? z.y + z.h / 2
                    : vAlign === "bottom"
                      ? z.y + z.h - padding
                      : z.y + padding + fontSize;
                const dominantBaseline =
                  vAlign === "middle" ? "middle" : vAlign === "bottom" ? "alphabetic" : "alphabetic";
                const lines = substituteTokens(z.text ?? "").split("\n");
                const clipId = `zone-clip-${z.id}`;
                return (
                  <g key={z.id} opacity={z.visible ? 1 : 0.35}>
                    <defs>
                      <clipPath id={clipId}>
                        <rect x={z.x} y={z.y} width={z.w} height={z.h} />
                      </clipPath>
                    </defs>
                    <rect
                      x={z.x}
                      y={z.y}
                      width={z.w}
                      height={z.h}
                      fill={sel ? "rgba(255,251,214,0.65)" : "rgba(255,255,255,0.5)"}
                      stroke={sel ? "#663399" : "#232323"}
                      strokeWidth={sel ? 3 : 1}
                      onPointerDown={(e) => onPointerDownZone(e, z)}
                      onPointerMove={onZonePointerMove}
                      onPointerUp={onZonePointerUp}
                      onPointerCancel={onZonePointerUp}
                      style={{ cursor: "move" }}
                    />

                    {/* live in-zone content preview at panel-true scale */}
                    {isTextLike && z.text && (
                      <g clipPath={`url(#${clipId})`} pointerEvents="none">
                        {lines.map((line, lineIdx) => {
                          const segs = segmentEmojis(line);
                          const lineY = textBaselineY + lineIdx * fontSize * 1.15;
                          // rough char width for positioning emoji images
                          const emojiSize = Math.round(fontSize * 0.95);
                          const apiBase = SERVER_URL || "";
                          // We compute a simple cumulative x so emoji <image>
                          // elements sit correctly inline with text tspans.
                          const estimatedCharW = fontSize * 0.6;
                          let cursorX = textX;
                          if (textAnchor === "middle") {
                            const approxW = segs.reduce(
                              (w, s) => w + (s.kind === "emoji" ? emojiSize : s.s.length * estimatedCharW),
                              0
                            );
                            cursorX = textX - approxW / 2;
                          } else if (textAnchor === "end") {
                            const approxW = segs.reduce(
                              (w, s) => w + (s.kind === "emoji" ? emojiSize : s.s.length * estimatedCharW),
                              0
                            );
                            cursorX = textX - approxW;
                          }
                          return (
                            <g key={lineIdx}>
                              {segs.map((seg, sIdx) => {
                                if (seg.kind === "text") {
                                  const el = (
                                    <text
                                      key={sIdx}
                                      x={cursorX}
                                      y={lineY}
                                      fontSize={fontSize}
                                      fontWeight={z.fontWeight === "bold" ? 700 : 400}
                                      fill={snapToSpectra6(z.color || "#000000")}
                                      textAnchor="start"
                                      dominantBaseline={dominantBaseline}
                                      style={{ userSelect: "none", fontFamily: cssFamilyForZone(z.fontFamily) }}
                                    >
                                      {seg.s}
                                    </text>
                                  );
                                  cursorX += seg.s.length * estimatedCharW;
                                  return el;
                                }
                                const el = (
                                  <image
                                    key={sIdx}
                                    x={cursorX}
                                    y={lineY - emojiSize * 0.82}
                                    width={emojiSize}
                                    height={emojiSize}
                                    href={`${apiBase}/api/aotd-irl/admin/emoji/${seg.cp}.png`}
                                  />
                                );
                                cursorX += emojiSize;
                                return el;
                              })}
                            </g>
                          );
                        })}
                      </g>
                    )}

                    {isEmojis && (
                      <g clipPath={`url(#${clipId})`} pointerEvents="none">
                        <text
                          x={z.x + z.w / 2}
                          y={z.y + z.h / 2}
                          fontSize={fontSize}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          style={{ userSelect: "none" }}
                        >
                          {"🎨 ✨ 🖼️"}
                        </text>
                      </g>
                    )}

                    {z.kind === "qr" && (
                      <g clipPath={`url(#${clipId})`} pointerEvents="none">
                        {/* schematic QR placeholder: 5x5 checkerboard + payload label */}
                        {(() => {
                          const side = Math.min(z.w, z.h) * 0.8;
                          const offX = z.x + (z.w - side) / 2;
                          const offY = z.y + (z.h - side) / 2;
                          const cell = side / 7;
                          const squares: Array<{ r: number; c: number }> = [
                            { r: 0, c: 0 }, { r: 0, c: 2 }, { r: 0, c: 4 }, { r: 0, c: 6 },
                            { r: 1, c: 1 }, { r: 1, c: 3 }, { r: 1, c: 5 },
                            { r: 2, c: 0 }, { r: 2, c: 2 }, { r: 2, c: 4 }, { r: 2, c: 6 },
                            { r: 3, c: 1 }, { r: 3, c: 3 }, { r: 3, c: 5 },
                            { r: 4, c: 0 }, { r: 4, c: 2 }, { r: 4, c: 4 }, { r: 4, c: 6 },
                            { r: 5, c: 1 }, { r: 5, c: 3 }, { r: 5, c: 5 },
                            { r: 6, c: 0 }, { r: 6, c: 2 }, { r: 6, c: 4 }, { r: 6, c: 6 },
                          ];
                          const fg = snapToSpectra6(z.color || "#000000");
                          const bg = snapToSpectra6(z.bgColor || "#FFFFFF");
                          const label =
                            z.qrPayload && z.qrPayload.trim()
                              ? z.qrPayload.length > 28
                                ? z.qrPayload.slice(0, 26) + "…"
                                : z.qrPayload
                              : "auto (claim URL in setup modes)";
                          return (
                            <>
                              <rect x={offX} y={offY} width={side} height={side} fill={bg} stroke="#888" />
                              {squares.map((s, i) => (
                                <rect
                                  key={i}
                                  x={offX + s.c * cell}
                                  y={offY + s.r * cell}
                                  width={cell}
                                  height={cell}
                                  fill={fg}
                                />
                              ))}
                              <text
                                x={z.x + z.w / 2}
                                y={offY + side + Math.min(14, z.h - side - 4)}
                                fontSize={Math.max(10, Math.min(14, z.w * 0.04))}
                                fill="#333"
                                textAnchor="middle"
                                dominantBaseline="alphabetic"
                                style={{ userSelect: "none", fontFamily: "ui-monospace, monospace" }}
                              >
                                {label}
                              </text>
                            </>
                          );
                        })()}
                      </g>
                    )}

                    {/* tiny corner label so we still know what zone this is */}
                    <text
                      x={z.x + 3}
                      y={z.y + 11}
                      fontSize={9}
                      fill="#666"
                      pointerEvents="none"
                      opacity={sel ? 1 : 0.55}
                      style={{ userSelect: "none", fontFamily: "ui-monospace, monospace" }}
                    >
                      {z.kind}:{z.id.slice(0, 6)}
                    </text>

                    {sel && (
                      <rect
                        x={z.x + z.w - HANDLE}
                        y={z.y + z.h - HANDLE}
                        width={HANDLE}
                        height={HANDLE}
                        fill="#232323"
                        stroke="#fff"
                        strokeWidth={1}
                        onPointerDown={(e) => onPointerDownHandle(e, z)}
                        onPointerMove={onZonePointerMove}
                        onPointerUp={onZonePointerUp}
                        onPointerCancel={onZonePointerUp}
                        style={{ cursor: "nwse-resize" }}
                      />
                    )}
                  </g>
                );
              })}
            </CanvasEl>
          </SvgHost>
        </CanvasCard>

        <Sidebar>
          <Row>
            <Btn type="button" onClick={addZone}>
              + Add zone
            </Btn>
            <Btn
              type="button"
              onClick={() => selectedId && duplicateZone(selectedId)}
              disabled={!selectedId}
              title="Duplicate selected (Cmd/Ctrl+D)"
            >
              Duplicate
            </Btn>
            <Btn type="button" onClick={undo} title="Undo (Cmd/Ctrl+Z)">
              Undo
            </Btn>
          </Row>

          <SidebarTitle>zones</SidebarTitle>
          <ZoneList>
            {zonesSorted.map((z, idx) => (
              <li
                key={z.id}
                data-sel={z.id === selectedId ? "1" : "0"}
                onClick={() => setSelectedId(z.id)}
                style={{ gap: 6 }}
              >
                <button
                  type="button"
                  title={z.visible ? "hide" : "show"}
                  onClick={(e) => {
                    e.stopPropagation();
                    updateZone(z.id, { visible: !z.visible });
                  }}
                  style={{
                    border: "1px solid #bbb",
                    background: z.visible ? "#fff" : "#eee",
                    cursor: "pointer",
                    padding: "0 4px",
                    fontSize: 11,
                    minWidth: 22,
                  }}
                >
                  {z.visible ? "👁" : "∅"}
                </button>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                  {z.kind} · {z.id.slice(0, 8)}
                </span>
                <button
                  type="button"
                  title="move up (higher z)"
                  disabled={idx === zonesSorted.length - 1}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveZoneZ(z.id, "up");
                  }}
                  style={{ border: "1px solid #bbb", background: "#fff", cursor: "pointer", padding: "0 4px", fontSize: 11 }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  title="move down (lower z)"
                  disabled={idx === 0}
                  onClick={(e) => {
                    e.stopPropagation();
                    moveZoneZ(z.id, "down");
                  }}
                  style={{ border: "1px solid #bbb", background: "#fff", cursor: "pointer", padding: "0 4px", fontSize: 11 }}
                >
                  ↓
                </button>
                <DelBtn
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteZone(z.id);
                  }}
                >
                  ×
                </DelBtn>
              </li>
            ))}
          </ZoneList>

          {selected && (
            <>
              <SidebarTitle>selected zone</SidebarTitle>
              <Field>
                kind
                <select
                  value={selected.kind}
                  onChange={(e) => updateZone(selected.id, { kind: e.target.value as ZoneKind })}
                >
                  {ZONE_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </Field>
              <Row>
                <Field style={{ flex: 1 }}>
                  x
                  <input
                    type="number"
                    value={Math.round(selected.x)}
                    onChange={(e) => updateZone(selected.id, { x: Number(e.target.value) })}
                  />
                </Field>
                <Field style={{ flex: 1 }}>
                  y
                  <input
                    type="number"
                    value={Math.round(selected.y)}
                    onChange={(e) => updateZone(selected.id, { y: Number(e.target.value) })}
                  />
                </Field>
              </Row>
              <Row>
                <Field style={{ flex: 1 }}>
                  w
                  <input
                    type="number"
                    value={Math.round(selected.w)}
                    onChange={(e) => updateZone(selected.id, { w: Number(e.target.value) })}
                  />
                </Field>
                <Field style={{ flex: 1 }}>
                  h
                  <input
                    type="number"
                    value={Math.round(selected.h)}
                    onChange={(e) => updateZone(selected.id, { h: Number(e.target.value) })}
                  />
                </Field>
              </Row>
              <Row>
                <Field style={{ flex: 1 }}>
                  z-index
                  <input
                    type="number"
                    value={selected.z}
                    onChange={(e) => updateZone(selected.id, { z: Number(e.target.value) })}
                  />
                </Field>
                <Field style={{ flex: 1, flexDirection: "row", alignItems: "center", gap: "0.35rem" }}>
                  <span>visible</span>
                  <input
                    type="checkbox"
                    checked={selected.visible}
                    onChange={(e) => updateZone(selected.id, { visible: e.target.checked })}
                  />
                </Field>
              </Row>
              <Field>
                rotation (°)
                <select
                  value={selected.rotation ?? 0}
                  onChange={(e) =>
                    updateZone(selected.id, { rotation: Number(e.target.value) as Zone["rotation"] })
                  }
                >
                  {ROTATIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </Field>

              {["text", "title", "author", "meta", "owner", "bottomBar"].includes(selected.kind) && (
                <>
                  <Field as="div">
                    text
                    <TokenPillEditor
                      value={selected.text ?? ""}
                      onChange={(next) => updateZone(selected.id, { text: next })}
                      allowedTokens={TOKENS_BY_KIND[selected.kind]}
                      sampleOf={(t) => TOKEN_SAMPLES[t as keyof typeof TOKEN_SAMPLES] ?? ""}
                      placeholder="type text; insert tokens below"
                    />
                  </Field>
                  <Field>
                    <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={!!selected.adaptive}
                        onChange={(e) => updateZone(selected.id, { adaptive: e.target.checked })}
                      />
                      adaptive (shrink to fit)
                    </span>
                    <small style={{ color: "#666", fontSize: "0.7rem" }}>
                      auto-reduces font size when text overflows the zone
                    </small>
                  </Field>
                  <Row>
                    <Field style={{ flex: 1 }}>
                      fontSize
                      <input
                        type="number"
                        value={selected.fontSize ?? ""}
                        onChange={(e) =>
                          updateZone(selected.id, {
                            fontSize: e.target.value === "" ? undefined : Number(e.target.value),
                          })
                        }
                      />
                    </Field>
                    <Field style={{ flex: 1 }}>
                      fontWeight
                      <select
                        value={selected.fontWeight ?? "normal"}
                        onChange={(e) =>
                          updateZone(selected.id, { fontWeight: e.target.value as Zone["fontWeight"] })
                        }
                      >
                        <option value="normal">normal</option>
                        <option value="bold">bold</option>
                      </select>
                    </Field>
                  </Row>
                  <Field>
                    fontFamily
                    <select
                      value={selected.fontFamily ?? DEFAULT_FONT_KEY}
                      onChange={(e) =>
                        updateZone(selected.id, {
                          fontFamily: e.target.value === DEFAULT_FONT_KEY ? undefined : e.target.value,
                        })
                      }
                    >
                      {fontFamilies.length === 0 ? (
                        <option value={DEFAULT_FONT_KEY}>JetBrains Mono (default)</option>
                      ) : (
                        fontFamilies.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}
                            {f.key === DEFAULT_FONT_KEY ? " (default)" : ""}
                            {f.monospace ? " · mono" : ""}
                          </option>
                        ))
                      )}
                    </select>
                  </Field>
                  <Row>
                    <Field style={{ flex: 1 }}>
                      align
                      <select
                        value={selected.align ?? "left"}
                        onChange={(e) => updateZone(selected.id, { align: e.target.value as Zone["align"] })}
                      >
                        <option value="left">left</option>
                        <option value="center">center</option>
                        <option value="right">right</option>
                      </select>
                    </Field>
                    <Field style={{ flex: 1 }}>
                      vAlign
                      <select
                        value={selected.vAlign ?? "top"}
                        onChange={(e) =>
                          updateZone(selected.id, { vAlign: e.target.value as Zone["vAlign"] })
                        }
                      >
                        <option value="top">top</option>
                        <option value="middle">middle</option>
                        <option value="bottom">bottom</option>
                      </select>
                    </Field>
                  </Row>
                  <Field>
                    color
                    <ColorInputRow
                      value={selected.color}
                      onChange={(v) => updateZone(selected.id, { color: v })}
                    />
                  </Field>
                  <Field>
                    padding
                    <input
                      type="number"
                      value={selected.padding ?? ""}
                      onChange={(e) =>
                        updateZone(selected.id, {
                          padding: e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                  {(() => {
                    // Panel-true preview: scale the zone's real pixel rect down to
                    // fit the inspector, apply the same scale to fontSize/padding,
                    // and snap the chosen colour to the nearest Spectra-6 primary
                    // so the on-screen result matches what the e-paper will show.
                    const PREVIEW_MAX_W = 360;
                    const PREVIEW_MAX_H = 520;
                    const zoneW = Math.max(1, selected.w);
                    const zoneH = Math.max(1, selected.h);
                    const fitScale = Math.min(PREVIEW_MAX_W / zoneW, PREVIEW_MAX_H / zoneH, 1);
                    const scale = fitScale * textPreviewZoom;
                    const previewW = zoneW * scale;
                    const previewH = zoneH * scale;
                    const fontSizePx = (selected.fontSize ?? 22) * scale;
                    const paddingPx = (selected.padding ?? 0) * scale;
                    const snapped = snapToSpectra6(selected.color || "#000000");
                    const align = selected.align ?? "left";
                    const vAlign = selected.vAlign ?? "top";
                    return (
                      <>
                        <TextPreviewLabel>
                          panel-true preview · zone {zoneW}×{zoneH}px · font{" "}
                          {selected.fontSize ?? 22}px · colour{" "}
                          <span style={{ fontFamily: "ui-monospace" }}>
                            {(selected.color || "#000000").toUpperCase()}
                          </span>
                          {" → "}
                          <span style={{ fontFamily: "ui-monospace", color: snapped }}>
                            {snapped}
                          </span>{" "}
                          (spectra-6 snap)
                          {selected.adaptive ? (
                            <em style={{ marginLeft: "0.25rem" }}>
                              · will auto-shrink in render if it overflows
                            </em>
                          ) : null}
                        </TextPreviewLabel>
                        <PreviewZoomBar>
                          <span>zoom</span>
                          {([1, 2, 3, 4] as const).map((z) => (
                            <PreviewZoomBtn
                              key={z}
                              type="button"
                              active={textPreviewZoom === z}
                              onClick={() => setTextPreviewZoom(z)}
                            >
                              {z}x
                            </PreviewZoomBtn>
                          ))}
                          <span style={{ color: "#888" }}>
                            · rendered at {Math.round(previewW)}×{Math.round(previewH)} css-px
                          </span>
                        </PreviewZoomBar>
                        <TextPreviewBox
                          style={{
                            width: previewW,
                            height: previewH,
                            minHeight: "unset",
                            maxHeight: "unset",
                            padding: paddingPx,
                            overflow: "hidden",
                            justifyContent:
                              align === "center"
                                ? "center"
                                : align === "right"
                                  ? "flex-end"
                                  : "flex-start",
                            alignItems:
                              vAlign === "middle"
                                ? "center"
                                : vAlign === "bottom"
                                  ? "flex-end"
                                  : "flex-start",
                            background: "#ffffff",
                          }}
                        >
                          <div
                            style={{
                              fontFamily: cssFamilyForZone(selected.fontFamily),
                              fontSize: `${fontSizePx}px`,
                              fontWeight: selected.fontWeight === "bold" ? 700 : 400,
                              color: snapped,
                              textAlign: align as "left" | "center" | "right",
                              lineHeight: 1.15,
                              width: "100%",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                            }}
                          >
                            {selected.text ? (
                              substituteTokens(selected.text)
                            ) : (
                              <span style={{ color: "#999" }}>(empty)</span>
                            )}
                          </div>
                        </TextPreviewBox>
                      </>
                    );
                  })()}
                </>
              )}

              {selected.kind === "image" && (
                <>
                  <Field>
                    imageFit
                    <select
                      value={selected.imageFit ?? "cover"}
                      onChange={(e) =>
                        updateZone(selected.id, { imageFit: e.target.value as Zone["imageFit"] })
                      }
                    >
                      <option value="cover">cover</option>
                      <option value="contain">contain</option>
                    </select>
                  </Field>
                  <Row>
                    <Field style={{ flex: 1 }}>
                      offsetX
                      <input
                        type="number"
                        value={selected.imageOffsetX ?? ""}
                        onChange={(e) =>
                          updateZone(selected.id, {
                            imageOffsetX: e.target.value === "" ? undefined : Number(e.target.value),
                          })
                        }
                      />
                    </Field>
                    <Field style={{ flex: 1 }}>
                      offsetY
                      <input
                        type="number"
                        value={selected.imageOffsetY ?? ""}
                        onChange={(e) =>
                          updateZone(selected.id, {
                            imageOffsetY: e.target.value === "" ? undefined : Number(e.target.value),
                          })
                        }
                      />
                    </Field>
                  </Row>
                </>
              )}

              {selected.kind === "qr" && (
                <>
                  <Field>
                    qr payload
                    <textarea
                      value={selected.qrPayload ?? ""}
                      onChange={(e) =>
                        updateZone(selected.id, { qrPayload: e.target.value || undefined })
                      }
                      placeholder="leave empty to auto-fill (see below)"
                      rows={3}
                      style={{ fontFamily: "inherit", resize: "vertical" }}
                    />
                  </Field>
                  <small style={{ color: "#555", fontSize: 11, lineHeight: 1.45 }}>
                    What this QR encodes at render time:
                    <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                      <li>
                        <strong>qr payload</strong> — this field, if set. Use it for static
                        content (WiFi URI, a URL, anything).
                      </li>
                      <li>
                        Otherwise, in <em>claim / onboarding</em> mode the backend fills in the
                        device&apos;s claim URL (<code>/aotd-irl/claim/:id?p=...</code>).
                      </li>
                      <li>
                        In <em>AOTD</em> mode, if no payload is set the QR will render blank —
                        set one explicitly if you want a QR on the daily poster.
                      </li>
                    </ul>
                  </small>
                  <Field>
                    color (QR foreground)
                    <ColorInputRow
                      value={selected.color}
                      onChange={(v) => updateZone(selected.id, { color: v })}
                    />
                  </Field>
                  <Field>
                    bgColor (QR background)
                    <ColorInputRow
                      value={selected.bgColor}
                      onChange={(v) => updateZone(selected.id, { bgColor: v })}
                    />
                  </Field>
                </>
              )}

              {(selected.kind === "rect" || selected.kind === "separator") && (
                <>
                  <Field>
                    color
                    <ColorInputRow
                      value={selected.color}
                      onChange={(v) => updateZone(selected.id, { color: v })}
                    />
                  </Field>
                  {selected.kind === "rect" && (
                    <Field>
                      bgColor
                      <ColorInputRow
                        value={selected.bgColor}
                        onChange={(v) => updateZone(selected.id, { bgColor: v })}
                      />
                    </Field>
                  )}
                </>
              )}

              {selected.kind === "emojis" && (
                <>
                  <Field>
                    align
                    <select
                      value={selected.align ?? "center"}
                      onChange={(e) => updateZone(selected.id, { align: e.target.value as Zone["align"] })}
                    >
                      <option value="left">left</option>
                      <option value="center">center</option>
                      <option value="right">right</option>
                    </select>
                  </Field>
                  <Field>
                    fontSize
                    <input
                      type="number"
                      value={selected.fontSize ?? ""}
                      onChange={(e) =>
                        updateZone(selected.id, {
                          fontSize: e.target.value === "" ? undefined : Number(e.target.value),
                        })
                      }
                    />
                  </Field>
                </>
              )}

              <DeleteZoneBtn
                type="button"
                onClick={async () => {
                  const ok = await confirm({
                    title: "Delete this zone?",
                    message: "This cannot be undone (unless you haven't saved — reset restores it).",
                    confirmLabel: "Delete",
                    tone: "danger",
                  });
                  if (ok) deleteZone(selected.id);
                }}
              >
                Delete zone
              </DeleteZoneBtn>
            </>
          )}

          {previewUrl && (
            <>
              <SidebarTitle>preview</SidebarTitle>
              {previewMeta.rotation && previewMeta.rotation !== 0 ? (
                <PreviewMetaNote style={{ color: "#0b61d9", marginBottom: "0.35rem" }}>
                  ⟳ preview shown UPRIGHT — device displays rotated{" "}
                  <strong>{previewMeta.rotation}°</strong>
                </PreviewMetaNote>
              ) : null}
              <PreviewBox>
                {previewLoading ? <PreviewStatus>rendering…</PreviewStatus> : null}
                <img src={previewUrl} alt="layout preview" />
              </PreviewBox>
              {previewMeta.effectiveDate ? (
                <PreviewMetaNote>
                  {previewMeta.usedFallback ? (
                    <span style={{ color: "#b45309" }}>
                      ⚠ today&apos;s aotd not available —{" "}
                    </span>
                  ) : null}
                  using aotd from {new Date(previewMeta.effectiveDate).toLocaleDateString()}
                </PreviewMetaNote>
              ) : null}
            </>
          )}
        </Sidebar>
      </Grid>
    </Wrap>
  );
}
